import { useState, useRef, useCallback, useMemo } from 'react';
import rawData from '../../assets/data/tendencia_deforestacion_mensual.json';
import rawAccumulatedData from '../../assets/data/tendencia_deforestacion_mensual_acumulada.json';

const MONTH_LABELS = [
  'ENE',
  'FEB',
  'MAR',
  'ABR',
  'MAY',
  'JUN',
  'JUL',
  'AGO',
  'SEP',
  'OCT',
  'NOV',
  'DIC',
];

const YEAR_COLORS = [
  '#06E89F',
  '#4ECDC4',
  '#5869C5',
  '#F5A623',
  '#E85D75',
  '#9B59B6',
  '#3498DB',
  '#1ABC9C',
];

const CHART_MODE_LABELS = {
  monthly: 'Detalle mensual',
  grouped: 'Comparación por mes',
  annual: 'Resumen anual',
};

const getMonthValue = (year, month) => {
  const entry = rawData.features.find(
    (f) => Number(f.properties.year) === year && f.properties.month === month
  );
  return entry ? parseFloat(Number(entry.properties.monthly_ha).toFixed(2)) : 0;
};

const calcThreshold = (values) => {
  const max = Math.max(...values, 0);
  return Math.max(1, parseFloat((max * 0.6).toFixed(1)));
};

const getChartMode = (yearCount) => {
  if (yearCount <= 1) return 'monthly';
  if (yearCount <= 3) return 'grouped';
  return 'annual';
};

const parseMonthlyData = (year) => {
  const values = MONTH_LABELS.map((_, i) =>
    getMonthValue(year, String(i + 1).padStart(2, '0'))
  );
  const threshold = calcThreshold(values);

  return {
    labels: MONTH_LABELS,
    values,
    threshold,
    series: [{ name: 'Deforestación (ha)', data: values }],
    colors: values.map((v) => (v >= threshold ? '#F5A623' : '#06E89F')),
    distributed: true,
    columnWidth: '62%',
    showLegend: false,
    xLabelRotate: 0,
    height: 280,
  };
};

const parseGroupedData = (years) => {
  const sortedYears = [...years].sort();
  const series = sortedYears.map((year, idx) => ({
    name: String(year),
    data: MONTH_LABELS.map((_, i) =>
      getMonthValue(year, String(i + 1).padStart(2, '0'))
    ),
    color: YEAR_COLORS[idx % YEAR_COLORS.length],
  }));

  const allValues = series.flatMap((s) => s.data);
  const threshold = calcThreshold(allValues);

  return {
    labels: MONTH_LABELS,
    threshold,
    series: series.map((s) => ({ name: s.name, data: s.data })),
    colors: series.map((s) => s.color),
    distributed: false,
    columnWidth: `${Math.max(55, 85 - sortedYears.length * 8)}%`,
    showLegend: true,
    xLabelRotate: 0,
    height: 320,
  };
};

const parseAnnualData = (years) => {
  const sortedYears = [...years].sort();
  const summary = sortedYears.map((year) => {
    const months = MONTH_LABELS.map((label, i) => {
      const val = getMonthValue(year, String(i + 1).padStart(2, '0'));
      return { month: label, val };
    });
    const total = parseFloat(
      months.reduce((sum, m) => sum + m.val, 0).toFixed(2)
    );
    const peak = months.reduce(
      (best, m) => (m.val > best.val ? m : best),
      months[0]
    );

    return {
      year,
      total,
      peakMonth: peak.month,
      peakValue: peak.val,
    };
  });

  const values = summary.map((s) => s.total);
  const threshold = calcThreshold(values);

  return {
    labels: sortedYears.map(String),
    values,
    threshold,
    summary,
    series: [{ name: 'Total anual (ha)', data: values }],
    colors: values.map((v) => (v >= threshold ? '#F5A623' : '#06E89F')),
    distributed: true,
    columnWidth: '55%',
    showLegend: false,
    xLabelRotate: 0,
    height: 300,
  };
};

const buildChartOptions = ({
  labels,
  threshold,
  colors,
  distributed,
  columnWidth,
  showLegend,
  xLabelRotate,
  chartMode,
}) => ({
  chart: {
    type: 'bar',
    toolbar: { show: false },
    background: 'transparent',
    foreColor: '#4a6a8a',
    animations: { enabled: true, speed: 600 },
  },
  plotOptions: {
    bar: {
      borderRadius: 3,
      distributed,
      columnWidth,
    },
  },
  colors,
  legend: showLegend
    ? {
        show: true,
        position: 'top',
        horizontalAlign: 'left',
        fontSize: '11px',
        fontFamily: 'MontserratSemiBold',
        labels: { colors: '#8aa8c8' },
        markers: { width: 10, height: 10, radius: 2 },
      }
    : { show: false },
  dataLabels: { enabled: false },
  grid: {
    borderColor: '#0d2040',
    strokeDashArray: 4,
    yaxis: { lines: { show: true } },
    xaxis: { lines: { show: false } },
  },
  annotations: {
    yaxis: [
      {
        y: threshold,
        borderColor: '#F5A623',
        borderWidth: 1,
        strokeDashArray: 6,
        label: {
          borderColor: 'transparent',
          style: {
            background: 'transparent',
            color: '#F5A623',
            fontSize: '9px',
            fontFamily: 'MontserratSemiBold',
          },
          text: `UMBRAL CRÍTICO (${threshold} HA)`,
          position: 'right',
          offsetX: -8,
        },
      },
    ],
  },
  xaxis: {
    categories: labels,
    axisBorder: { show: false },
    axisTicks: { show: false },
    labels: {
      rotate: xLabelRotate,
      style: { colors: '#4a6a8a', fontSize: '10px', fontFamily: 'Montserrat' },
    },
  },
  yaxis: {
    show: true,
    labels: {
      style: { colors: '#4a6a8a', fontSize: '9px', fontFamily: 'Montserrat' },
      formatter: (v) => `${v} ha`,
    },
  },
  tooltip: {
    theme: 'dark',
    shared: chartMode === 'grouped',
    intersect: chartMode !== 'grouped',
    y: { formatter: (v) => `${v} ha` },
  },
});

const METRICS_COPY = {
  monthly:
    'Vista mensual de un solo año. Las barras naranjas superan el umbral crítico calculado sobre el máximo del período seleccionado.',
  grouped:
    'Comparación mes a mes entre los años elegidos. Cada color representa un año distinto para facilitar la lectura.',
  annual:
    'Con 4 o más años, el gráfico resume el total anual. La tabla inferior muestra el mes pico de cada año.',
};

const SENTINEL_YEARS = [2018, 2019, 2020, 2021, 2022, 2023, 2024, 2025];

const AwaiLogic = () => {
  const [activeTab, setActiveTab] = useState('vision');
  const [sliderPos, setSliderPos] = useState(42);
  const [selectedYears, setSelectedYears] = useState([2022, 2023]);
  const [sentinelYear, setSentinelYear] = useState(2023);
  const imageCompareRef = useRef(null);
  const isDragging = useRef(false);

  const sentinelDeforImg = `/deforestation/deforestacion_${sentinelYear}.jpg`;

  const startDrag = useCallback(() => {
    isDragging.current = true;
  }, []);
  const stopDrag = useCallback(() => {
    isDragging.current = false;
  }, []);

  const onMouseMove = useCallback((e) => {
    if (!isDragging.current || !imageCompareRef.current) return;
    const rect = imageCompareRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    setSliderPos(Math.round((x / rect.width) * 100));
  }, []);

  const onTouchMove = useCallback((e) => {
    if (!imageCompareRef.current) return;
    const touch = e.touches[0];
    const rect = imageCompareRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(touch.clientX - rect.left, rect.width));
    setSliderPos(Math.round((x / rect.width) * 100));
  }, []);

  const chartMode = getChartMode(selectedYears.length);

  const chartData = useMemo(() => {
    if (chartMode === 'monthly') return parseMonthlyData(selectedYears[0]);
    if (chartMode === 'grouped') return parseGroupedData(selectedYears);
    return parseAnnualData(selectedYears);
  }, [chartMode, selectedYears]);

  const monthlyBarOptions = useMemo(
    () => buildChartOptions({ ...chartData, chartMode }),
    [chartData, chartMode]
  );

  const monthlyBarSeries = chartData.series;
  const chartHeight = chartData.height;
  const annualSummary = chartData.summary || null;
  const chartModeLabel = CHART_MODE_LABELS[chartMode];
  const metricsDescription = METRICS_COPY[chartMode];

  const yearRangeLabel =
    selectedYears.length === 1
      ? String(selectedYears[0])
      : `${Math.min(...selectedYears)} – ${Math.max(...selectedYears)}`;

  const availableYears = [
    ...new Set(rawData.features.map((f) => Number(f.properties.year))),
  ].sort();

  const { accLabels, accValues, totalHa } = useMemo(() => {
    const entries = rawAccumulatedData.features
      .map((f) => f.properties)
      .sort((a, b) => a.date.localeCompare(b.date));

    const yearlyEntries = entries.filter((p) => p.month === '12');

    const labels = yearlyEntries.map((p) => p.year);

    const values = yearlyEntries.map((p) => Number(p.cumulative_ha.toFixed(2)));

    const total =
      values.length > 0 ? values[values.length - 1].toFixed(2) : '0.00';

    return {
      accLabels: labels,
      accValues: values,
      totalHa: total,
    };
  }, []);

  const accumulatedOptions = {
    chart: {
      type: 'area',
      toolbar: { show: false },
      background: 'transparent',
      foreColor: '#4a6a8a',
      animations: { enabled: true, speed: 900 },
    },
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth', width: 2, colors: ['#06E89F'] },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        colorStops: [
          { offset: 0, color: '#06E89F', opacity: 0.35 },
          { offset: 100, color: '#06E89F', opacity: 0.02 },
        ],
      },
    },
    markers: {
      size: 4,
      colors: ['#06E89F'],
      strokeColors: '#071020',
      strokeWidth: 2,
      hover: { size: 6 },
    },
    grid: { borderColor: '#0d2040', strokeDashArray: 4 },
    xaxis: {
      categories: accLabels,
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: {
        rotate: -45,
        style: { colors: '#4a6a8a', fontSize: '9px', fontFamily: 'Montserrat' },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: '#4a6a8a',
          fontSize: '10px',
          fontFamily: 'Montserrat',
        },
        formatter: (v) => `${v} ha`,
      },
    },
    tooltip: { theme: 'dark', y: { formatter: (v) => `${v} ha` } },
  };

  const accumulatedSeries = [{ name: 'Acumulada (ha)', data: accValues }];

  return {
    activeTab,
    setActiveTab,
    sliderPos,
    imageCompareRef,
    startDrag,
    stopDrag,
    onMouseMove,
    onTouchMove,
    monthlyBarSeries,
    monthlyBarOptions,
    accumulatedSeries,
    accumulatedOptions,
    selectedYears,
    setSelectedYears,
    yearRangeLabel,
    availableYears,
    totalHa,
    chartMode,
    chartModeLabel,
    chartHeight,
    annualSummary,
    metricsDescription,
    sentinelYear,
    setSentinelYear,
    sentinelDeforImg,
    SENTINEL_YEARS,
  };
};

export default AwaiLogic;
